import { render } from "@react-email/render";
import emailApi from "@/api/email";
import DeleteRequestConfirmationEmail from "@/email/emails/DeleteRequestConfirmationEmail";
import DocumentAttachmentEmail from "@/email/emails/DocumentAttachmentEmail";
import ErrorReportEmail from "@/email/emails/ErrorReportEmail";
import FeedbackEmail from "@/email/emails/FeedbackEmail";
import PasswordChangedEmail from "@/email/emails/PasswordChangedEmail";
import ReferralSuccessEmail from "@/email/emails/ReferralSuccessEmail";
import ResetPasswordEmail from "@/email/emails/ResetPasswordEmail";
import SurveyEmail from "@/email/emails/SurveyEmail";
import VerifyEmail from "@/email/emails/VerifyEmail";
import { User } from "@/types";
import { DeviceType } from "@/types/device";
import { AppRoutes } from "@/types/routes";
import { ITenant } from "@/types/tenant";

const sendEmail = async (
    html: string,
    subject: string,
    emailTo: string,
    attachment?: File | Blob,
    fileName?: string,
) => {
    const options = {
        to: emailTo,
        subject,
        html,
        attachment,
        fileName,
    };

    try {
        await emailApi.sendEmail(options);
    } catch (err) {
        console.error(err);
        throw err;
    }
};

export const sendPasswordResetEmail = async (
    tenant: ITenant | null,
    emailTo: string,
    resetPasswordLink: string,
    name: string,
) => {
    try {
        const emailHtml = render(
            ResetPasswordEmail({
                resetPasswordLink: resetPasswordLink,
                name,
                tenant,
            }),
        );
        return sendEmail(emailHtml, "Reset your password", emailTo);
    } catch (err) {
        console.error(err);
        return err;
    }
};

export const sendVerifyEmail = async (
    tenant: ITenant | null,
    emailTo: string,
    code: string,
) => {
    const emailHtml = render(VerifyEmail({ code, tenant }));
    return sendEmail(emailHtml, "Verify your email", emailTo);
};

export const sendPasswordChangedEmail = async (
    tenant: ITenant | null,
    emailTo: string,
    name: string,
) => {
    const emailHtml = render(
        PasswordChangedEmail({
            name,
            appDomain: `${window.location.origin}`,
            tenant,
        }),
    );
    return sendEmail(emailHtml, "Password changed", emailTo);
};

export const sendDeleteRequestConfirmationEmail = async (
    tenant: ITenant | null,
    emailTo: string,
    loginUrl: string,
) => {
    const emailHtml = render(
        DeleteRequestConfirmationEmail({ loginUrl, tenant }),
    );
    return sendEmail(emailHtml, "Account deletion confirmation", emailTo);
};

export const sendErrorReport = async (
    tenant: ITenant | null,
    error: unknown,
    errorInfo: unknown,
    feedback: string,
    url: string,
    user?: User,
) => {
    try {
        const emailHtml = render(
            ErrorReportEmail({ error, errorInfo, user, feedback, url, tenant }),
        );
        return sendEmail(emailHtml, "Error Report", "hello@1template.io");
    } catch (err) {
        console.error(err);
        return err;
    }
};

export const sendFeedbackEmail = async (
    tenant: ITenant | null,
    feedback: string,
    url: string,
    user?: User,
) => {
    try {
        const emailHtml = render(
            FeedbackEmail({ user, feedback, url, tenant }),
        );
        return sendEmail(emailHtml, "User Feedback", "hello@1template.io");
    } catch (err) {
        console.error(err);
        return err;
    }
};

export const sendFeedbackSurveyEmail = async (
    tenant: ITenant | null,
    user: User,
    url: string,
    satisfaction: number,
    nps: number,
    deviceType: DeviceType,
    browserDetails: string,
    comments?: string,
) => {
    const subject = `Survey Feedback - ${user.email}`;
    const emailHtml = render(
        SurveyEmail({
            tenant,
            user,
            url,
            satisfaction,
            nps,
            comments,
            deviceType,
            browserDetails,
        }),
    );
    return sendEmail(emailHtml, subject, "hello@1template.io");
};

interface SendDocumentOptions {
    tenant: ITenant | null;
    documentFile: Blob;
    fileName: string;
    user: User;
}

export const sendDocumentAttachmentEmail = async ({
    tenant,
    documentFile,
    fileName,
    user,
}: SendDocumentOptions) => {
    if (!documentFile || !user?.email) {
        throw new Error("Document file and user email are required");
    }

    try {
        const emailHtml = render(DocumentAttachmentEmail({ user, tenant }));
        return await sendEmail(
            emailHtml,
            "Your Resume is Ready!",
            user.email,
            documentFile,
            fileName,
        );
    } catch (error) {
        console.error("Failed to send document attachment email:", error);
        throw error;
    }
};

interface SendReferralSuccessEmailOptions {
    tenant: ITenant | null;
    emailTo: string;
    name: string;
    creditsEarned: number;
    referralLimit: number;
    referredUserName: string;
}

export const sendReferralSuccessEmail = async ({
    tenant,
    emailTo,
    name,
    creditsEarned,
    referralLimit,
    referredUserName,
}: SendReferralSuccessEmailOptions) => {
    try {
        const emailHtml = render(
            ReferralSuccessEmail({
                tenant,
                inviteLink: `${window.location.origin}/${AppRoutes.Profile}?tab=invite`,
                referredUserName,
                creditsEarned,
                name,
                referralLimit,
            }),
        );
        return await sendEmail(emailHtml, "You've Earned Credits!", emailTo);
    } catch (error) {
        console.error("Failed to send referral success email:", error);
        throw error;
    }
};
